import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  fetchAccessibleWorkspaces, 
  fetchUserProfile, 
  WorkspaceInfo,
  getActiveWorkspace, 
  saveActiveWorkspace, 
  clearActiveWorkspace,
  isValidWorkspace,
  getFirstAvailableWorkspace 
} from '@umd-dit/workspaces-component-react';

interface User {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  is_admin?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  user_id: string | null;
  isAdmin: boolean;
  selectedOrganizationId: string | null;
  selectedWorkspaceId: string | null;
  accessibleWorkspaces: WorkspaceInfo[];
  currentWorkspace: WorkspaceInfo | null;
  selectWorkspace: (org_id: string, ws_id: string) => void;
  selectOrganization: (org_id: string) => void;
  loadingWorkspaces: boolean;
  workspacesError: string | null;
  hasWorkspaceAccess: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Use the dedicated workspaces API URL for workspace and user profile calls
const WORKSPACES_API_URL = import.meta.env.VITE_WORKSPACES_API_URL;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user_id, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [accessibleWorkspaces, setAccessibleWorkspaces] = useState<WorkspaceInfo[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [workspacesError, setWorkspacesError] = useState<string | null>(null);

  useEffect(() => {
    // Handle authentication state changes from OIDC component
    const handleAuthStateChange = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { authenticated, user } = customEvent.detail;
      console.log('[AuthContext] Auth state changed:', authenticated);

      setIsAuthenticated(authenticated);
      setUser(user);

      if (authenticated && user) {
        // Extract user ID from the user object, considering all possible paths in OIDC user object
        let extractedUserId = null;

        if (user.sub) {
          extractedUserId = user.sub;
        } else if (user.profile?.sub) {
          extractedUserId = user.profile.sub;
        } else if (user.userInfo?.sub) {
          extractedUserId = user.userInfo.sub;
        } else if (user.preferred_username) {
          extractedUserId = user.preferred_username;
        } else if (user.profile?.preferred_username) {
          extractedUserId = user.profile.preferred_username;
        } else if (user.userInfo?.username) {
          extractedUserId = user.userInfo.username;
        } else if (user.username) {
          extractedUserId = user.username;
        } else if (user.email) {
          extractedUserId = user.email;
        }

        if (extractedUserId) {
          console.log('[AuthContext] User ID set:', extractedUserId);
          setUserId(extractedUserId);

          try {
            // Fetch user profile and workspaces
            console.log('[AuthContext] Fetching user data...');
            setLoadingWorkspaces(true);

            const userProfile = await fetchUserProfile(WORKSPACES_API_URL, 'legislative-review', 'umd-org');
            console.log('[AuthContext] User profile loaded:', userProfile);
            setIsAdmin(userProfile.is_admin);
            
            // Lock non-admins to their organization
            if (!userProfile.is_admin && userProfile.org_id) {
              console.log('[AuthContext] Non-admin user locked to org:', userProfile.org_id);
              setSelectedOrganizationId(userProfile.org_id);
            }

            const workspaces = await fetchAccessibleWorkspaces(WORKSPACES_API_URL, 'legislative-review', 'umd-org');
            console.log('[AuthContext] Workspaces loaded:', workspaces.length);
            setAccessibleWorkspaces(workspaces);

            // Get and validate saved workspace selection
            const savedWorkspace = getActiveWorkspace();
            if (savedWorkspace) {
              const { org_id, ws_id } = savedWorkspace;
              if (isValidWorkspace(org_id, ws_id, workspaces)) {
                console.log('[AuthContext] Using saved workspace selection:', { org_id, ws_id });
                setSelectedOrganizationId(org_id);
                setSelectedWorkspaceId(ws_id);
              } else {
                console.log('[AuthContext] Clearing invalid workspace selection');
                clearActiveWorkspace();
                
                // If available, use the first workspace instead
                const firstWorkspace = getFirstAvailableWorkspace(workspaces);
                if (firstWorkspace) {
                  console.log('[AuthContext] Using first available workspace:', firstWorkspace);
                  setSelectedOrganizationId(firstWorkspace.org_id);
                  setSelectedWorkspaceId(firstWorkspace.ws_id);
                  saveActiveWorkspace(firstWorkspace.org_id, firstWorkspace.ws_id);
                }
              }
            } else if (workspaces.length > 0) {
              // No selection was saved, but workspaces are available - use the first one
              const firstWorkspace = getFirstAvailableWorkspace(workspaces);
              if (firstWorkspace) {
                console.log('[AuthContext] Using first available workspace as default:', firstWorkspace);
                setSelectedOrganizationId(firstWorkspace.org_id);
                setSelectedWorkspaceId(firstWorkspace.ws_id);
                saveActiveWorkspace(firstWorkspace.org_id, firstWorkspace.ws_id);
              }
            }

            // If user has no workspaces, show access denied (unless they're an admin)
            if (workspaces.length === 0 && !userProfile.is_admin) {
              console.log('[AuthContext] No accessible workspaces - access will be denied');
            }

            setWorkspacesError(null);
          } catch (error) {
            console.error('[AuthContext] Error fetching user data:', error);
            setWorkspacesError('Failed to load user data');
          } finally {
            setLoadingWorkspaces(false);
          }
        } else {
          console.error('[AuthContext] Could not extract user_id from user object');
          setWorkspacesError('Failed to extract user ID from authentication data');
        }
      }

      setIsLoading(false);
    };

    // Listen for both auth-state-change (from our custom event) and access-granted (from OIDC component)
    document.addEventListener('auth-state-change', handleAuthStateChange);
    document.addEventListener('access-granted', handleAuthStateChange);

    return () => {
      document.removeEventListener('auth-state-change', handleAuthStateChange);
      document.removeEventListener('access-granted', handleAuthStateChange);
    };
  }, []);

  // Function to select a workspace
  const selectWorkspace = (org_id: string, ws_id: string) => {
    console.log('[AuthContext] Selecting workspace:', { org_id, ws_id });
    
    // Trust the WorkspaceSelector component - it only shows valid workspaces
    setSelectedOrganizationId(org_id);
    setSelectedWorkspaceId(ws_id);
    
    // Persist to localStorage using the utility function
    saveActiveWorkspace(org_id, ws_id);
  };

  const selectOrganization = (org_id: string) => {
    console.log('[AuthContext] Selecting organization:', org_id);
    setSelectedOrganizationId(org_id);
    // When switching organizations, clear the selected workspace
    setSelectedWorkspaceId(null);
    // Clear saved workspace for the old org
    if (selectedOrganizationId) {
      localStorage.removeItem(`activeWorkspace_${selectedOrganizationId}`);
    }
  };

  const hasWorkspaceAccess = Boolean(selectedWorkspaceId) || isAdmin;

  // Find the current workspace info
  const currentWorkspace = accessibleWorkspaces.find(
    ws => ws.org_id === selectedOrganizationId && ws.ws_id === selectedWorkspaceId
  ) || null;

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    user_id,
    isAdmin,
    selectedOrganizationId,
    selectedWorkspaceId,
    accessibleWorkspaces,
    currentWorkspace,
    selectWorkspace,
    selectOrganization,
    loadingWorkspaces,
    workspacesError,
    hasWorkspaceAccess
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}